import dayjs, { type Dayjs } from "dayjs";
import { UCUM_STEPS } from "@/services/fhir/observation/wearableObservationBuilder";
import {
  EXERCISE_SESSION_CODE,
  formatWearableObservationValue,
  getObservationComponentRows,
  getObservationPrimaryCode,
  getSleepSkinTempDisplay,
  getSleepTotalDisplay,
  collectSpO2Candidates,
  resolveSpO2Parts,
  getWearableObservationDayKey,
  getWearableObservationLabel,
  getWearableObservationSectionId,
  type WearableObservationSectionId,
} from "@/utils/wearableObservationFormat";
import { metricLabelSortIndex } from "./metricDisplayOrder";

/** Valor de um ponto no gráfico do período. */
export interface DailyPoint {
  /**
   * Dia efetivo (YYYY-MM-DD) — ao clicar abre o detalhe do dia.
   * Em exercício pode haver vários pontos com o mesmo dateKey.
   */
  dateKey: string;
  /** Rótulo do eixo (ex.: 03/08). */
  dateLabel: string;
  /**
   * Chave única no Recharts (evita misturar tooltip quando há 2+ pontos no mesmo dia).
   */
  pointId: string;
  value: number;
  formatted: string;
  /** Exercício: tipo da atividade. Temperatura: horário no hover. */
  activityLabel?: string;
}

/**
 * Como resumir o período no cartão:
 * - average: média dos pontos
 * - last: último registro do período (peso e altura)
 */
export type PeriodSummaryMode = "average" | "last";

/** Uma métrica ao longo do período, com resumo e série. */
export interface MetricSeries {
  key: string;
  sectionId: WearableObservationSectionId;
  label: string;
  points: DailyPoint[];
  average: number;
  formattedAverage: string;
  daysWithData: number;
  summaryMode: PeriodSummaryMode;
  /** Unidade interna da série (ex.: clock = eixo HH:mm). */
  valueUnit?: string;
  valueCode?: string;
}

/** last/sum = 1 ponto/dia; session = 1 ponto por sessão (vários no mesmo dia). */
type DayMode = "last" | "sum" | "session";

const LAST_VALUE_LOINC_CODES = new Set(["29463-7", "8302-2"]);

interface Sample {
  key: string;
  label: string;
  value: number;
  unit?: string;
  code?: string;
  mode: DayMode;
  observationCode: string;
  sessionTime?: number;
  /** Exercício: tipo da atividade. Temperatura: horário (mín/máx) no hover. */
  activityLabel?: string;
}

const CLOCK_VALUE_UNIT = "clock";

function numeric(value: unknown): number | null {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

const WHOLE_NUMBER_UNITS = new Set([
  "count",
  "1",
  UCUM_STEPS,
  "beats/min",
  "/min",
  "mg/dL",
  "mg/dl",
]);

/** DD/MM a partir de YYYY-MM-DD sem shift de timezone. */
function formatDayMonthLabel(dateKey: string): string {
  const parts = dateKey.split("-");
  if (parts.length >= 3) return `${parts[2]}/${parts[1]}`;
  return dayjs(dateKey).format("DD/MM");
}

/** Minutos desde meia-noite → HH:mm (gráficos de início/fim do sono). */
export function formatClockMinutes(value: number): string {
  const normalized = ((Math.round(value) % (24 * 60)) + 24 * 60) % (24 * 60);
  const hours = Math.floor(normalized / 60);
  const minutes = normalized % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

function timeToClockMinutes(iso: string | undefined): number | null {
  if (!iso) return null;
  const parsed = dayjs(iso);
  if (!parsed.isValid()) return null;
  return parsed.hour() * 60 + parsed.minute();
}

function formatValue(value: number, unit?: string, code?: string): string {
  if (unit === "clock" || code === "clock") {
    return formatClockMinutes(value);
  }

  const isWholeNumber =
    WHOLE_NUMBER_UNITS.has(unit ?? "") || WHOLE_NUMBER_UNITS.has(code ?? "");
  const rounded = isWholeNumber
    ? Math.round(value)
    : Math.round(value * 10) / 10;
  return formatWearableObservationValue({
    valueQuantity: { value: rounded, unit, code },
  });
}

function componentCode(component: any): string {
  const coding = Array.isArray(component?.code?.coding)
    ? component.code.coding
    : [];
  const found = coding.find((c: any) => c?.code);
  return found?.code ? String(found.code) : "";
}

function componentLabel(component: any): string {
  return (
    component?.code?.text || component?.code?.coding?.[0]?.display || "Item"
  );
}

function isExerciseTypeComponent(component: any): boolean {
  const label = componentLabel(component)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  return (
    label.includes("tipo de exercicio") ||
    label.includes("exercise type") ||
    label.includes("activity type") ||
    (label.includes("tipo") && label.includes("exerc"))
  );
}

/**
 * Tipo da atividade — mesma fonte da visão do dia (getObservationComponentRows).
 */
function exerciseActivityLabel(resource: any): string | undefined {
  const rows = getObservationComponentRows(resource);
  const typeRow = rows.find((row) => {
    const normalized = row.label
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    return (
      normalized.includes("tipo de exercicio") ||
      normalized.includes("exercise type") ||
      normalized.includes("activity type") ||
      (normalized.includes("tipo") && normalized.includes("exerc"))
    );
  });
  const value = typeRow?.value?.trim();
  return value && value !== "-" ? value : undefined;
}

function sampleTime(resource: any): number {
  // Horário de envio (chips "Envio"); lastUpdated só como reserva
  const raw =
    resource?.issued ??
    resource?.meta?.lastUpdated ??
    resource?.effectiveDateTime ??
    resource?.effectivePeriod?.end ??
    resource?.effectivePeriod?.start;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.valueOf() : 0;
}

function exerciseSessionTime(resource: any): number {
  const raw =
    resource?.effectivePeriod?.start ??
    resource?.effectiveDateTime ??
    resource?.issued ??
    resource?.meta?.lastUpdated;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.valueOf() : 0;
}

/** Durações de sono (podem vir em segundos no app). */
const SLEEP_DURATION_CODES = new Set([
  "93832-4",
  "93828-2",
  "93829-0",
  "93830-8",
  "93831-6",
  "96634-8",
]);

/**
 * Dia do gráfico / detalhe — delega ao helper compartilhado
 * (sono = início da sessão; tela = fim do uso).
 */
export function sleepWakeDay(resource: any): Dayjs | null {
  const key = getWearableObservationDayKey(resource);
  if (!key) return null;
  // Só faz sentido para LOINCs de sono; mantém API usada fora deste arquivo
  const parsed = dayjs(key);
  return parsed.isValid() ? parsed : null;
}

/**
 * Dia do ponto no gráfico (mesma regra do detalhe do dia).
 */
function observationDate(resource: any): Dayjs | null {
  const key = getWearableObservationDayKey(resource);
  if (!key) return null;
  const parsed = dayjs(key);
  return parsed.isValid() ? parsed : null;
}

/** Chave do lote de envio (mesmo minuto ≈ mesmo sync). */
function issuedBatchKey(resource: any): string {
  const raw = resource?.issued ?? resource?.meta?.lastUpdated;
  const parsed = dayjs(raw);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD HH:mm") : "";
}

/** Dia efetivo + código da Observation (água, exercício, tela…). */
function dayMetricScopeKey(resource: any): string | null {
  const date = observationDate(resource);
  if (!date) return null;
  const code = getObservationPrimaryCode(resource) || "unknown";
  return `${date.format("YYYY-MM-DD")}:${code}`;
}

/**
 * Por dia e por tipo de dado, mantém só o último envio.
 * Assim um sync só de água não apaga exercício, tela, etc. do mesmo dia.
 * Reenvio do mesmo tipo (ex.: Other duas vezes) continua sem repetir.
 */
function filterExerciseToLastIssuedPerDay(entries: any[]): any[] {
  const bestIssuedByScope = new Map<string, number>();

  entries.forEach((entry) => {
    const resource = entry?.resource;
    if (getObservationPrimaryCode(resource) !== EXERCISE_SESSION_CODE) return;
    const scope = dayMetricScopeKey(resource);
    if (!scope) return;
    const issued = sampleTime(resource);
    const current = bestIssuedByScope.get(scope) ?? 0;
    if (issued > current) bestIssuedByScope.set(scope, issued);
  });

  const bestBatchByScope = new Map<string, string>();
  entries.forEach((entry) => {
    const resource = entry?.resource;
    if (getObservationPrimaryCode(resource) !== EXERCISE_SESSION_CODE) return;
    const scope = dayMetricScopeKey(resource);
    if (!scope) return;
    const bestIssued = bestIssuedByScope.get(scope);
    if (bestIssued == null || sampleTime(resource) !== bestIssued) return;
    const batch = issuedBatchKey(resource);
    if (batch) bestBatchByScope.set(scope, batch);
  });

  return entries.filter((entry) => {
    const resource = entry?.resource;
    // Passos/água/tela: não filtrar por lote (podem estar no 2º horário)
    if (getObservationPrimaryCode(resource) !== EXERCISE_SESSION_CODE) {
      return true;
    }
    const scope = dayMetricScopeKey(resource);
    if (!scope) return false;
    const bestBatch = bestBatchByScope.get(scope);
    if (!bestBatch) {
      return sampleTime(resource) === (bestIssuedByScope.get(scope) ?? -1);
    }
    return issuedBatchKey(resource) === bestBatch;
  });
}

function extractSamples(resource: any): Sample[] {
  const code = getObservationPrimaryCode(resource);
  const label = getWearableObservationLabel(resource);
  const components = Array.isArray(resource?.component)
    ? resource.component
    : [];

  // Exercício: cartões genéricos (Exercício · Duração, Calorias…).
  // Sem separar por tipo; cada sessão = um ponto no gráfico.
  if (code === EXERCISE_SESSION_CODE) {
    const sessionTime = exerciseSessionTime(resource);
    const activityLabel = exerciseActivityLabel(resource);
    return components.flatMap((component: any): Sample[] => {
      if (isExerciseTypeComponent(component)) return [];
      const value = numeric(component?.valueQuantity?.value);
      if (value === null) return [];
      const metricCode = componentCode(component) || componentLabel(component);
      return [
        {
          key: `${code}:${metricCode}`,
          label: `Exercício · ${componentLabel(component)}`,
          value,
          unit: component?.valueQuantity?.unit,
          code: component?.valueQuantity?.code,
          mode: "session",
          observationCode: code,
          sessionTime,
          activityLabel,
        },
      ];
    });
  }

  // Temperatura corporal: 3 séries (mínima → média → máxima), igual à visão do dia
  if (code === "81264-4") {
    const temp = getSleepSkinTempDisplay(resource);
    const unit = temp.unit || "°C";
    const samples: Sample[] = [];

    if (temp.minValue !== null) {
      samples.push({
        key: `${code}:min`,
        label: "Temperatura corporal · Mínima",
        value: temp.minValue,
        unit,
        code: unit,
        mode: "last",
        observationCode: code,
        activityLabel: temp.minTime ? `às ${temp.minTime}` : undefined,
      });
    }
    if (temp.avgValue !== null) {
      samples.push({
        key: `${code}:avg`,
        label: "Temperatura corporal · Média",
        value: temp.avgValue,
        unit,
        code: unit,
        mode: "last",
        observationCode: code,
        activityLabel: temp.avgTime ? `às ${temp.avgTime}` : undefined,
      });
    }
    if (temp.maxValue !== null) {
      samples.push({
        key: `${code}:max`,
        label: "Temperatura corporal · Máxima",
        value: temp.maxValue,
        unit,
        code: unit,
        mode: "last",
        observationCode: code,
        activityLabel: temp.maxTime ? `às ${temp.maxTime}` : undefined,
      });
    }
    return samples;
  }

  // SpO2 no período é agregado por dia (várias Observations → mín/média)
  if (code === "59408-5") {
    return [];
  }

  // Sono total: duração, início e fim — cada um vira cartão na seção Sono.
  if (code === "93832-4") {
    const sleep = getSleepTotalDisplay(resource);
    const unit =
      resource?.valueQuantity?.unit || resource?.valueQuantity?.code;
    let minutes = numeric(
      resource?.valueQuantity?.value ?? resource?.valueInteger
    );
    if (minutes !== null && unit === "s") {
      minutes = minutes / 60;
    }

    const samples: Sample[] = [];
    if (minutes !== null) {
      samples.push({
        key: code,
        label: "Sono total · Duração",
        value: minutes,
        unit: "min",
        code: "min",
        mode: "last",
        observationCode: code,
      });
    }

    const startMinutes = timeToClockMinutes(resource?.effectivePeriod?.start);
    if (startMinutes !== null) {
      samples.push({
        key: `${code}:start`,
        label: "Sono total · Início",
        value: startMinutes,
        unit: CLOCK_VALUE_UNIT,
        code: CLOCK_VALUE_UNIT,
        mode: "last",
        observationCode: code,
        activityLabel: sleep.start ? `às ${sleep.start}` : undefined,
      });
    }

    const endMinutes = timeToClockMinutes(resource?.effectivePeriod?.end);
    if (endMinutes !== null) {
      samples.push({
        key: `${code}:end`,
        label: "Sono total · Fim",
        value: endMinutes,
        unit: CLOCK_VALUE_UNIT,
        code: CLOCK_VALUE_UNIT,
        mode: "last",
        observationCode: code,
        activityLabel: sleep.end ? `às ${sleep.end}` : undefined,
      });
    }

    return samples;
  }

  // Estágios / SpO2 <90%: mesmos minutos do dia; segundos → minutos
  if (SLEEP_DURATION_CODES.has(code) && code !== "93832-4") {
    const unit =
      resource?.valueQuantity?.unit || resource?.valueQuantity?.code;
    // Aceita valueQuantity ou valueInteger (syncs antigos do app)
    let minutes = numeric(
      resource?.valueQuantity?.value ?? resource?.valueInteger
    );
    if (minutes !== null && unit === "s") {
      minutes = minutes / 60;
    }
    if (minutes === null) return [];
    return [
      {
        key: code,
        label,
        value: minutes,
        unit: "min",
        code: "min",
        mode: "last",
        observationCode: code,
      },
    ];
  }

  const mainValue = numeric(resource?.valueQuantity?.value);
  if (mainValue !== null) {
    return [
      {
        key: code || label,
        label,
        value: mainValue,
        unit: resource?.valueQuantity?.unit,
        code: resource?.valueQuantity?.code,
        mode: "last",
        observationCode: code,
      },
    ];
  }

  return components.flatMap((component: any): Sample[] => {
    const value = numeric(component?.valueQuantity?.value);
    if (value === null) return [];
    const cCode = componentCode(component);
    if (cCode.startsWith("app-usage")) return [];
    return [
      {
        key: `${code}:${cCode}`,
        label: `${label} · ${componentLabel(component)}`,
        value,
        unit: component?.valueQuantity?.unit,
        code: component?.valueQuantity?.code,
        mode: "last",
        observationCode: code,
      },
    ];
  });
}

/**
 * Por dia: do envio mais novo ao mais antigo, fica o primeiro valor de cada
 * métrica. Sync posterior sem o dado não apaga o dia; sync anterior com o
 * dado continua contando (mesma regra que funcionou no tempo de tela).
 */
function resolveDaySamplesNewestFirst(
  dayEntries: Array<{ time: number; samples: Sample[] }>
): Sample[] {
  const ordered = [...dayEntries].sort((a, b) => b.time - a.time);
  const byKey = new Map<string, Sample>();
  ordered.forEach(({ samples }) => {
    samples.forEach((sample) => {
      if (!byKey.has(sample.key)) byKey.set(sample.key, sample);
    });
  });
  return Array.from(byKey.values());
}

/**
 * Séries do período (7/15/30 dias).
 * Regra geral (igual tempo de tela):
 * - 1 ponto por dia de uso que tiver registro
 * - por métrica, último envio que tiver o valor (mesmo em horário inferior)
 * - dia sem dado: não entra no gráfico nem na média
 * - média = soma dos dias com dado ÷ quantidade desses dias
 * Exercício continua por sessão. Peso/altura: último valor real.
 */
export function buildPeriodSeries(
  entries: any[],
  rangeDays = 7,
  periodEndKey?: string
): MetricSeries[] {
  interface DayCell {
    value: number;
    time: number;
    activityLabel?: string;
  }

  interface SessionPoint {
    dateKey: string;
    dateLabel: string;
    pointId: string;
    value: number;
    time: number;
    activityLabel?: string;
  }

  interface Bucket {
    label: string;
    sectionId: WearableObservationSectionId;
    unit?: string;
    code?: string;
    mode: DayMode;
    observationCode: string;
    days: Map<string, DayCell>;
    sessions: SessionPoint[];
  }

  type DayBatch = {
    time: number;
    samples: Sample[];
    sectionId: WearableObservationSectionId;
  };

  // Dias permitidos no intervalo selecionado (ex.: 7 dias até periodEndKey)
  const allowedDays = (() => {
    if (!periodEndKey || rangeDays <= 0) return null;
    const end = dayjs(periodEndKey);
    if (!end.isValid()) return null;
    const keys = new Set<string>();
    for (let i = 0; i < rangeDays; i++) {
      keys.add(end.subtract(i, "day").format("YYYY-MM-DD"));
    }
    return keys;
  })();

  const inSelectedRange = (dateKey: string) =>
    !allowedDays || allowedDays.has(dateKey);

  const buckets = new Map<string, Bucket>();
  const scopedEntries = filterExerciseToLastIssuedPerDay(entries);

  type SpO2DayValue = {
    value: number;
    time: number;
    role: "min" | "avg" | "unknown";
    unit?: string;
    code?: string;
  };
  const spo2ByDay = new Map<string, SpO2DayValue[]>();

  // Geral / sono / tela / composição: lotes por dia de uso
  const dayBatches = new Map<string, DayBatch[]>();

  scopedEntries.forEach((entry: any) => {
    const resource = entry?.resource;
    const date = observationDate(resource);
    if (!date) return;

    const dateKey = date.format("YYYY-MM-DD");
    // Descarta dia de uso fora do intervalo (ex.: tela com period.end no dia anterior)
    if (!inSelectedRange(dateKey)) return;

    const time = sampleTime(resource);
    const sectionId = getWearableObservationSectionId(resource);
    const primaryCode = getObservationPrimaryCode(resource);

    if (primaryCode === "59408-5") {
      const dayValues = spo2ByDay.get(dateKey) ?? [];
      collectSpO2Candidates(resource).forEach((candidate) => {
        dayValues.push({
          value: candidate.value,
          time,
          role: candidate.role,
          unit: candidate.unit,
          code: candidate.code,
        });
      });
      spo2ByDay.set(dateKey, dayValues);
      return;
    }

    const samples = extractSamples(resource);
    if (samples.length === 0) return;

    // Exercício: um ponto por sessão
    if (samples.some((sample) => sample.mode === "session")) {
      samples.forEach((sample) => {
        if (sample.mode !== "session") return;
        const bucket = buckets.get(sample.key) ?? {
          label: sample.label,
          sectionId,
          unit: sample.unit,
          code: sample.code,
          mode: sample.mode,
          observationCode: sample.observationCode,
          days: new Map<string, DayCell>(),
          sessions: [],
        };
        const sessionAt = sample.sessionTime ?? time;
        const sessionDay = dayjs(sessionAt);
        const pointDateKey = sessionDay.isValid()
          ? sessionDay.format("YYYY-MM-DD")
          : dateKey;
        if (!inSelectedRange(pointDateKey)) return;
        bucket.sessions.push({
          dateKey: pointDateKey,
          dateLabel: formatDayMonthLabel(pointDateKey),
          pointId: `${pointDateKey}-${sessionAt}-${sample.key}-${bucket.sessions.length}`,
          value: sample.value,
          time: sessionAt,
          activityLabel: sample.activityLabel,
        });
        buckets.set(sample.key, bucket);
      });
      return;
    }

    const batch = dayBatches.get(dateKey) ?? [];
    batch.push({ time, samples, sectionId });
    dayBatches.set(dateKey, batch);
  });

  const sectionForSample = (batches: DayBatch[], sampleKey: string) =>
    [...batches]
      .filter((entry) => entry.samples.some((s) => s.key === sampleKey))
      .sort((a, b) => b.time - a.time)[0]?.sectionId;

  // Resolve diário: último envio que tiver cada métrica
  dayBatches.forEach((batches, dateKey) => {
    resolveDaySamplesNewestFirst(batches).forEach((sample) => {
      const sectionId =
        sectionForSample(batches, sample.key) ?? "general";

      const bucket = buckets.get(sample.key) ?? {
        label: sample.label,
        sectionId,
        unit: sample.unit,
        code: sample.code,
        mode: sample.mode,
        observationCode: sample.observationCode,
        days: new Map<string, DayCell>(),
        sessions: [],
      };

      bucket.sectionId = sectionId;

      if (sample.mode === "sum") {
        const current = bucket.days.get(dateKey);
        const latestTime = Math.max(
          current?.time ?? 0,
          ...batches.map((b) => b.time)
        );
        bucket.days.set(dateKey, {
          value: (current?.value ?? 0) + sample.value,
          time: latestTime,
          activityLabel: sample.activityLabel ?? current?.activityLabel,
        });
      } else {
        const latestWithMetric = [...batches]
          .filter((entry) => entry.samples.some((s) => s.key === sample.key))
          .sort((a, b) => b.time - a.time)[0];
        bucket.days.set(dateKey, {
          value: sample.value,
          time: latestWithMetric?.time ?? 0,
          activityLabel: sample.activityLabel,
        });
      }

      buckets.set(sample.key, bucket);
    });
  });

  // SpO2 por dia
  const spo2Code = "59408-5";
  spo2ByDay.forEach((dayValues, dateKey) => {
    const { min, avg } = resolveSpO2Parts(
      dayValues.map((item) => ({
        value: item.value,
        formatted: formatValue(item.value, item.unit ?? "%", item.code ?? "%"),
        role: item.role,
        unit: item.unit,
        code: item.code,
      }))
    );
    const latestTime = dayValues.reduce(
      (max, item) => Math.max(max, item.time),
      0
    );
    const pushSpO2 = (
      key: string,
      label: string,
      part: { value: number; unit?: string; code?: string } | undefined
    ) => {
      if (!part) return;
      const unit = part.unit ?? "%";
      const code = part.code ?? "%";
      const bucket = buckets.get(key) ?? {
        label,
        sectionId: "sleep" as WearableObservationSectionId,
        unit,
        code,
        mode: "last" as DayMode,
        observationCode: spo2Code,
        days: new Map<string, DayCell>(),
        sessions: [],
      };
      bucket.days.set(dateKey, { value: part.value, time: latestTime });
      buckets.set(key, bucket);
    };
    pushSpO2(`${spo2Code}:min`, "SpO2 · Mínima", min);
    pushSpO2(`${spo2Code}:avg`, "SpO2 · Média", avg);
  });

  return Array.from(buckets.entries())
    .map(([key, bucket]) => {
      const isLastValue = LAST_VALUE_LOINC_CODES.has(bucket.observationCode);
      const isSession = bucket.mode === "session";

      let points: DailyPoint[] = [];
      let summaryMode: PeriodSummaryMode = isLastValue ? "last" : "average";
      let summaryValue = 0;

      if (isSession) {
        const ordered = [...bucket.sessions]
          .filter((session) => inSelectedRange(session.dateKey))
          .sort((a, b) => a.time - b.time);
        points = ordered.map((session) => ({
          dateKey: session.dateKey,
          dateLabel: session.dateLabel,
          pointId: session.pointId,
          value: session.value,
          formatted: formatValue(session.value, bucket.unit, bucket.code),
          activityLabel: session.activityLabel,
        }));
        const total = points.reduce((sum, point) => sum + point.value, 0);
        summaryValue = points.length > 0 ? total / points.length : 0;
        summaryMode = "average";
      } else {
        // Só dias com registro real e dentro do intervalo escolhido
        const dayEntries = Array.from(bucket.days.entries())
          .filter(
            ([dateKey, cell]) => cell.time > 0 && inSelectedRange(dateKey)
          )
          .sort(([a], [b]) => a.localeCompare(b));

        points = dayEntries.map(([dateKey, { value, activityLabel }]) => ({
          dateKey,
          dateLabel: formatDayMonthLabel(dateKey),
          pointId: dateKey,
          value,
          formatted: formatValue(value, bucket.unit, bucket.code),
          activityLabel,
        }));

        if (summaryMode === "last") {
          if (dayEntries.length > 0) {
            const lastDay = dayEntries.reduce((latest, current) =>
              current[1].time >= latest[1].time ? current : latest
            );
            summaryValue = lastDay[1].value;
          }
        } else {
          // Soma dos dias com dado ÷ quantidade desses dias (não conta dia vazio)
          const total = points.reduce((sum, point) => sum + point.value, 0);
          summaryValue = points.length > 0 ? total / points.length : 0;
        }
      }

      const uniqueDays = new Set(points.map((point) => point.dateKey));

      return {
        key,
        sectionId: bucket.sectionId,
        label: bucket.label,
        points,
        average: summaryValue,
        formattedAverage: formatValue(summaryValue, bucket.unit, bucket.code),
        daysWithData: uniqueDays.size,
        summaryMode,
        valueUnit: bucket.unit,
        valueCode: bucket.code,
      };
    })
    .filter((series) => series.points.length > 0)
    .sort((a, b) => {
      if (
        (a.sectionId === "exercise" || a.sectionId === "screenTime") &&
        a.sectionId === b.sectionId
      ) {
        return 0;
      }
      const diff =
        metricLabelSortIndex(a.label, a.key) -
        metricLabelSortIndex(b.label, b.key);
      if (diff !== 0) return diff;
      return a.label.localeCompare(b.label, "pt-BR");
    });
}
